import React, { useEffect, useContext } from 'react';
import { bindActionCreators, Dispatch } from 'redux';
import { connect, ConnectedProps } from 'react-redux';
import { AccessContext } from '@piwikpro/platform';
import { Spinner, SplashScreen } from '@piwikpro/ui-components';
import { useParams } from '@tanstack/react-router';
import { AppPermissionPass } from '../firewall/AppPermissionPass';
import { AppPermissionsState, checkAppPermissions as checkAppPermissionsPure } from '../reducer';

interface SecuredAppsOwnProps {
  component: React.ComponentType<any>
}

const mapStateToProps = (state: { apps: AppPermissionsState }) => ({
  levels: state.apps.levels,
  isLoading: state.apps.isLoading,
});

const mapDispatchToProps = (dispatch: Dispatch) => ({
  checkAppPermissions: bindActionCreators(checkAppPermissionsPure, dispatch),
});

const connector = connect(mapStateToProps, mapDispatchToProps);

type ReduxProps = ConnectedProps<typeof connector>;

type SecuredAppsProps = SecuredAppsOwnProps & ReduxProps;

const SecuredApps: React.FC<SecuredAppsProps> = ({
  checkAppPermissions,
  component: Component,
  levels,
  isLoading,
  ...rest
}) => {
  // strict: false merges all registered routes' params; cast to a generic map
  // so this component compiles in every app context (support-panel, astronauth)
  // where $appId routes don't exist.
  const params = useParams({ strict: false }) as Record<string, string | undefined>;
  const { accessManager, userRole } = useContext(AccessContext);

  useEffect(() => {
    if (params.appId && userRole) {
      checkAppPermissions(params.appId, userRole);
    }
  }, [params.appId, userRole, checkAppPermissions]);

  if (isLoading) {
    return (
      <Spinner type="centered" className="x-large" />
    );
  }

  if (!levels.includes('view')) {
    return (
      <SplashScreen type="noPrivileges" />
    );
  }

  return (
    <AccessContext.Provider
      value={{
        // @ts-ignore:next-line
        accessManager: accessManager?.create(
          new AppPermissionPass(levels),
        ),
        userPermission: levels[levels.length - 1],
      }}
    >
      <Component {...rest} />
    </AccessContext.Provider>
  );
};

export default connector(SecuredApps) as React.ComponentType<SecuredAppsOwnProps>;
